import React from 'react';
import { useForm } from 'react-hook-form';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import Button from '../UI/Button';

const LeadForm = ({ lead, onSubmit, onCancel }) => {
  const { services, users, leadSources } = useCRM();
  const { user: currentUser, isAdmin, isManager } = useAuth();
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: lead || {
      leadName: '',
      leadEmail: '',
      leadPhone: '',
      company: '',
      leadSourceId: '',
      requiredServices: [],
      status: 'pending',
      dateOfLead: new Date().toISOString().split('T')[0],
      contactedDate: '',
      feedbackFromClient: '',
      comments: '',
      reminderDateTime: '',
      assignedTo: '',
      estimatedValue: '',
      priority: 'medium'
    }
  });

  const statuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal_sent', label: 'Proposal Sent' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'converted', label: 'Converted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'lost', label: 'Lost' },
    { value: 'follow_up', label: 'Follow Up' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  const handleFormSubmit = (data) => {
    // Convert services array and prepare data
    const formattedData = {
      ...data,
      requiredServices: Array.isArray(data.requiredServices) 
        ? data.requiredServices 
        : data.requiredServices ? data.requiredServices.split(',').map(s => s.trim()) : [],
      estimatedValue: data.estimatedValue ? parseFloat(data.estimatedValue) : null,
      reminderDateTime: data.reminderDateTime || null,
      contactedDate: data.contactedDate || null,
      assignedTo: data.assignedTo || currentUser.id
    };
    
    onSubmit(formattedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Lead Name *
          </label>
          <input
            type="text"
            {...register('leadName', { 
              required: 'Lead name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter lead name"
          />
          {errors.leadName && (
            <p className="mt-1 text-sm text-red-600">{errors.leadName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company
          </label>
          <input
            type="text"
            {...register('company')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter company name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            {...register('leadEmail', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter email address"
          />
          {errors.leadEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.leadEmail.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            {...register('leadPhone')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter phone number"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Lead Source *
          </label>
          <select
            {...register('leadSourceId', { required: 'Lead source is required' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select lead source</option>
            {leadSources?.map(source => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
          {errors.leadSourceId && (
            <p className="mt-1 text-sm text-red-600">{errors.leadSourceId.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            {...register('status')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            {statuses.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <select
            {...register('priority')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            {priorities.map(priority => (
              <option key={priority.value} value={priority.value}>
                {priority.label}
              </option>
            ))}
          </select>
        </div>

        {(isAdmin || isManager) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign To
            </label>
            <select
              {...register('assignedTo')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select user</option>
              {users?.filter(user => user.isActive !== false).map(user => (
                <option key={user.id} value={user.id}>
                  {user.fullName || user.full_name} ({user.role})
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date of Lead
          </label>
          <input
            type="date"
            {...register('dateOfLead')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contacted Date
          </label>
          <input
            type="date"
            {...register('contactedDate')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estimated Value ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            {...register('estimatedValue')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter estimated value"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reminder Date & Time
          </label>
          <input
            type="datetime-local"
            {...register('reminderDateTime')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Set a reminder for follow-up calls or meetings
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Required Services
        </label>
        <textarea
          {...register('requiredServices')}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter required services separated by commas (e.g., Web Development, SEO, Digital Marketing)"
        />
        <p className="text-xs text-gray-500 mt-1">
          Separate multiple services with commas
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Feedback from Client
        </label>
        <textarea
          {...register('feedbackFromClient')}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter client feedback, requirements, or conversation notes"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Comments (Optional)
        </label>
        <textarea
          {...register('comments')}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          placeholder="Additional notes or comments"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {lead ? 'Update Lead' : 'Add Lead'}
        </Button>
      </div>
    </form>
  );
};

export default LeadForm;